import axios from "axios";
import { firebaseFunctionsUrl } from "../Helpers/FirebaseEnvironments";

const TEMPLATE_AUTHORING_DOC = `# Template Authoring (Markdown + optional rules)

## What is a template authoring file?

A template authoring file is the source-of-truth for humans.

It contains:
- A top-level **H1** title (template name)
- Optionally: an **Author** line (e.g., Author: John Doe)
- Optionally: an **Image** line with a public URL (e.g., Image: https://example.com/image.png)
- Optionally: a **NodeType** line to specify the default node type (e.g., NodeType: Card)
- Optionally: a **YAML** rules block in a fenced code section
- A Markdown **nested list** that represents the template's node hierarchy

## Template node type (optional)

Available node types:
- **Card** (default) - Standard rounded rectangle nodes
- **Note** - Circular note-style nodes
- **Center** - Center circular nodes (typically for map center)
- **Image** - Image nodes
- **MindMap** - Mind map style nodes

## The node tree (nested list)

Use a Markdown bullet list. Indentation defines parent → child.

INDENTATION RULES:
- Use Tab key or 2 spaces for each level of indentation
- Start with ONE root node (no indentation)
- All children must be indented exactly ONE level more than their parent
- Consistent indentation is critical

Example:
- Core Hypothesis
	- Key Decision 1
		- Knowledge Gap 1.1
			- Activity 1.1.1

### Notes lines

If you want explanatory text, add a child bullet with the prefix "Notes:".

Example:
- Core Hypothesis
	- Notes: The central hypothesis to explore.

## Optional rules block (suggestions-only)

If present, include a fenced YAML block:

\`\`\`yaml
rulesVersion: 1
suggestNextChild:
  - when:
      parentRole: core_hypothesis
    suggest:
      - role: key_decision
        labelTemplate: "Key Decision {n}"
        reason: "Break the hypothesis into key decisions."
\`\`\`

### What rules do

Rules are non-binding suggestions stored as templateRules. Users can override them during work via File → Template Rules.`;

export const fixTemplateWithAI = async (
  templateMarkdown: string,
): Promise<string> => {
  try {
    const prompt = `You are a FocusMap template validator and fixer. You MUST fix the markdown template provided by the user.

TEMPLATE AUTHORING GUIDELINES:
${TEMPLATE_AUTHORING_DOC}

USER'S TEMPLATE MARKDOWN (contains errors that need fixing):
\`\`\`markdown
${templateMarkdown}
\`\`\`

INSTRUCTIONS:
1. Fix ALL indentation errors - ensure proper hierarchy with tabs or 2-space indentation
2. Ensure there is ONE root node without any indentation
3. Fix any YAML syntax errors if present
4. Preserve all the user's content, labels, and Notes
5. DO NOT create a different template or explain what "undefined" means
6. DO NOT return JSON or any other format
7. Return ONLY the corrected MARKDOWN template

CRITICAL: Your response must be ONLY markdown text starting with # and containing the tree structure. Do not return JSON, explanations, or anything else.

Return the corrected markdown template now:`;

    axios.defaults.withCredentials = true;
    const res = await axios.post(firebaseFunctionsUrl("openai"), {
      query: {
        messages: [
          {
            role: "system",
            content:
              "You are a markdown template fixer. You only return corrected markdown templates. Never return JSON, explanations, or create new content. Only fix formatting and indentation errors in the provided template.",
          },
          {
            role: "user",
            content: prompt,
          },
        ],
      },
    });

    const completionContent = res.data.choices[0].message.content;

    if (completionContent) {
      // Check if response looks like JSON instead of markdown
      const trimmed = completionContent.trim();
      if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
        throw new Error(
          "AI returned invalid format (JSON instead of markdown). Please try again or fix manually.",
        );
      }

      // Clean up any markdown code block wrappers
      let cleaned = trimmed;
      if (cleaned.startsWith("```markdown")) {
        cleaned = cleaned.replace(/^```markdown\s*/, "").replace(/```\s*$/, "");
      } else if (cleaned.startsWith("```")) {
        cleaned = cleaned.replace(/^```\s*/, "").replace(/```\s*$/, "");
      }

      const result = cleaned.trim();

      // Validate that we got a markdown template (should start with # or contain -)
      if (!result.includes("#") && !result.includes("- ")) {
        throw new Error(
          "AI response doesn't look like a valid markdown template. Please try again or fix manually.",
        );
      }

      return result;
    } else {
      throw new Error("No response from AI");
    }
  } catch (error) {
    console.error("Error fixing template with AI:", error);
    throw error;
  }
};
